import React, { useState } from 'react';
import { CheckCircle2, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/features/ui/primitives/card';
import { Button } from '@/features/ui/primitives/button';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { logger } from '@/features/help/utils/logger';
import { InfoTooltip, Tooltip } from '@/features/ui/components/Tooltip';

// Local CRM logos
import salesforceLogo from '@/assets/images/logos/salesforce.png';
import zohoLogo from '@/assets/images/logos/zoho.png';
import hubspotLogo from '@/assets/images/logos/hubspot.png';
import gohighlevelLogo from '@/assets/images/logos/gohighlevel.png';

/**
 * Provider Selection Page component that replaces the modal approach
 * with a dedicated page for selecting CRM providers
 */
type Provider = {
  id: string;
  name: string;
  logo: string;
  color: string;
  disabled: boolean;
};

type SalesforceLoginMode = 'company-domain' | 'normal' | null;

const SALESFORCE_PROVIDER_ID = 'salesforce';
const SALESFORCE_MY_DOMAIN_SUFFIX = '.my.salesforce.com';
const SALESFORCE_LIGHTNING_DOMAIN_SUFFIX = '.lightning.force.com';
const SALESFORCE_SETUP_DOMAIN_SUFFIX = '.my.salesforce-setup.com';
const SALESFORCE_COMPANY_DOMAIN_TOOLTIP =
  'Enter only your Salesforce company prefix. If Salesforce shows yourcompany.lightning.force.com/lightning, enter yourcompany. If you see a setup URL like yourcompany.my.salesforce-setup.com, enter yourcompany.';

const extractSalesforceCompanyPrefix = (value: string): string => {
  const trimmedValue = value.trim().toLowerCase();

  if (!trimmedValue) {
    return '';
  }

  let normalizedValue = trimmedValue;

  try {
    const parsedUrl = new URL(
      /^https?:\/\//i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`
    );
    normalizedValue = parsedUrl.hostname.toLowerCase();
  } catch {
    normalizedValue = trimmedValue.split('/')[0].toLowerCase();
  }

  const sanitizedValue = normalizedValue.replace(/\.+$/, '');

  if (sanitizedValue.endsWith(SALESFORCE_LIGHTNING_DOMAIN_SUFFIX)) {
    return sanitizedValue.slice(0, -SALESFORCE_LIGHTNING_DOMAIN_SUFFIX.length);
  }

  if (sanitizedValue.endsWith(SALESFORCE_MY_DOMAIN_SUFFIX)) {
    return sanitizedValue.slice(0, -SALESFORCE_MY_DOMAIN_SUFFIX.length);
  }

  if (sanitizedValue.endsWith(SALESFORCE_SETUP_DOMAIN_SUFFIX)) {
    return sanitizedValue.slice(0, -SALESFORCE_SETUP_DOMAIN_SUFFIX.length);
  }

  return sanitizedValue;
};

const isValidSalesforceCompanyPrefix = (value: string): boolean =>
  /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(value);

const ProviderSelectionModal: React.FC = () => {
  const navigate = useNavigate();
  const { setSelectedProvider, initiateOAuth } = useAuthStore();
  const [salesforceCompanyPrefix, setSalesforceCompanyPrefix] = useState('');
  const [salesforceLoginMode, setSalesforceLoginMode] = useState<SalesforceLoginMode>(null);
  const [isSalesforceExpanded, setIsSalesforceExpanded] = useState(false);
  const [isSubmittingProvider, setIsSubmittingProvider] = useState<string | null>(null);

  const normalizedSalesforceCompanyPrefix = salesforceCompanyPrefix.trim().toLowerCase();
  const salesforceCompanyPrefixError =
    normalizedSalesforceCompanyPrefix &&
    !isValidSalesforceCompanyPrefix(normalizedSalesforceCompanyPrefix)
      ? 'Enter only the first part of your Salesforce domain, like yourcompany.'
      : null;
  const salesforceLoginDomain = salesforceCompanyPrefixError
    ? null
    : normalizedSalesforceCompanyPrefix
      ? `${normalizedSalesforceCompanyPrefix}${SALESFORCE_MY_DOMAIN_SUFFIX}`
      : null;

  const providers: Provider[] = [
    {
      id: SALESFORCE_PROVIDER_ID,
      name: 'Salesforce',
      logo: salesforceLogo,
      color: 'bg-blue-50 hover:bg-blue-100 border-blue-200',
      disabled: false,
    },
    {
      id: 'zoho',
      name: 'Zoho',
      logo: zohoLogo,
      color: 'bg-green-50 hover:bg-green-100 border-green-200',
      disabled: false,
    },
    {
      id: 'hubspot',
      name: 'HubSpot',
      logo: hubspotLogo,
      color: 'bg-orange-50 hover:bg-orange-100 border-orange-200',
      disabled: false,
    },
    {
      id: 'gohighlevel',
      name: 'Go High Level',
      logo: gohighlevelLogo,
      color: 'bg-purple-50 hover:bg-purple-100 border-purple-200',
      disabled: false,
    },
  ];

  const handleProviderSelect = async (
    providerId: string,
    options: { loginDomain?: string | null } = {}
  ) => {
    logger.info('ProviderSelectionModal', 'User selected CRM provider', { providerId });
    setSelectedProvider(providerId);
    setIsSubmittingProvider(providerId);

    try {
      await initiateOAuth(providerId, {
        loginDomain:
          providerId === SALESFORCE_PROVIDER_ID
            ? typeof options.loginDomain === 'string' && options.loginDomain.trim()
              ? options.loginDomain.trim()
              : null
            : null,
      });
      // OAuth will redirect away, no need to navigate manually
    } catch (error) {
      logger.error('ProviderSelectionModal', 'Failed to initiate OAuth', { providerId, error });
      // On error, navigate back to dashboard - the loader will handle the error
      navigate('/dashboard');
    } finally {
      setIsSubmittingProvider(null);
    }
  };

  const handleSalesforceCardClick = () => {
    if (isSubmittingProvider === SALESFORCE_PROVIDER_ID) {
      return;
    }

    setSelectedProvider(SALESFORCE_PROVIDER_ID);
    setIsSalesforceExpanded((currentValue) => !currentValue);
  };

  const handleSalesforceCompanyDomainSelect = () => {
    setSalesforceLoginMode('company-domain');
  };

  const handleSalesforceNormalLoginSelect = async () => {
    setSalesforceLoginMode('normal');
    await handleProviderSelect(SALESFORCE_PROVIDER_ID);
  };

  const handleSalesforceCompanyDomainContinue = async () => {
    if (!salesforceLoginDomain) {
      return;
    }

    await handleProviderSelect(SALESFORCE_PROVIDER_ID, {
      loginDomain: salesforceLoginDomain,
    });
  };

  const renderProvider = (provider: Provider) => {
    const isSalesforce = provider.id === SALESFORCE_PROVIDER_ID;
    const isExpanded = isSalesforce && isSalesforceExpanded;
    const isLoading = isSubmittingProvider === provider.id;

    const cardContent = (
      <Card
        className={`transition-all duration-200 ${provider.color} ${
          provider.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        } ${
          isExpanded ? 'border-blue-400 ring-2 ring-blue-100 shadow-md shadow-blue-100/60' : ''
        }`}
        onClick={
          provider.disabled
            ? undefined
            : isSalesforce
              ? handleSalesforceCardClick
              : () => void handleProviderSelect(provider.id)
        }
        aria-expanded={isSalesforce ? isExpanded : undefined}
        aria-controls={isSalesforce ? 'salesforce-login-options' : undefined}
      >
        <CardContent className="p-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center justify-center gap-4">
              <img
                src={provider.logo}
                alt={`${provider.name} logo`}
                className="w-8 h-8 object-contain"
              />
              <span className="text-lg font-medium">{provider.name}</span>
            </div>
            {isSalesforce ? (
              <ChevronDown
                className={`h-5 w-5 text-blue-600 transition-transform duration-300 ${
                  isExpanded ? 'rotate-180' : ''
                }`}
              />
            ) : null}
          </div>
        </CardContent>
      </Card>
    );

    if (provider.disabled) {
      return (
        <Tooltip text={`Coming soon!`} side="right">
          <div className="cursor-not-allowed">{cardContent}</div>
        </Tooltip>
      );
    }

    return (
      <div className="space-y-3">
        {cardContent}
        {isSalesforce ? (
          <div
            id="salesforce-login-options"
            className={`grid overflow-hidden transition-all duration-300 ease-out ${
              isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
            }`}
          >
            <div className="overflow-hidden">
              <div className="rounded-xl border border-blue-100 bg-white p-4 text-left shadow-sm">
                <p className="text-sm font-semibold text-slate-900">
                  How do you login to Salesforce?
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Choose the login path that matches your Salesforce sign-in experience.
                </p>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={handleSalesforceCompanyDomainSelect}
                    className={`rounded-xl border px-4 py-4 text-left transition-all duration-200 ${
                      salesforceLoginMode === 'company-domain'
                        ? 'border-blue-500 bg-blue-50 shadow-sm'
                        : 'border-slate-200 bg-slate-50 hover:border-blue-200 hover:bg-blue-50/60'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          I enter company domain first
                        </p>
                        <p className="mt-2 text-xs leading-5 text-slate-500">
                          Salesforce asks for your company domain before showing the standard
                          login page.
                        </p>
                      </div>
                      {salesforceLoginMode === 'company-domain' ? (
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
                      ) : null}
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => void handleSalesforceNormalLoginSelect()}
                    disabled={isLoading}
                    className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 text-left transition-all duration-200 hover:border-blue-200 hover:bg-blue-50/60 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <p className="text-sm font-semibold text-slate-900">I use normal login</p>
                    <p className="mt-2 text-xs leading-5 text-slate-500">
                      I go straight to the normal Salesforce email and password sign-in screen.
                    </p>
                  </button>
                </div>

                <div
                  className={`grid overflow-hidden transition-all duration-300 ease-out ${
                    salesforceLoginMode === 'company-domain'
                      ? 'mt-4 grid-rows-[1fr] opacity-100'
                      : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex items-center gap-2">
                        <label
                          htmlFor="salesforce-login-domain"
                          className="block text-sm font-medium text-slate-900"
                        >
                          Company prefix
                        </label>
                        <InfoTooltip
                          text={SALESFORCE_COMPANY_DOMAIN_TOOLTIP}
                          side="top"
                          align="start"
                        />
                      </div>
                      <div className="mt-2 flex overflow-hidden rounded-md border border-slate-300 bg-white shadow-sm transition-colors focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20">
                        <input
                          id="salesforce-login-domain"
                          type="text"
                          value={salesforceCompanyPrefix}
                          onChange={(event) =>
                            setSalesforceCompanyPrefix(
                              extractSalesforceCompanyPrefix(event.target.value)
                            )
                          }
                          placeholder="examplecompany"
                          className="min-w-0 flex-1 border-0 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-0"
                        />
                        <div className="border-l border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-500">
                          {SALESFORCE_MY_DOMAIN_SUFFIX}
                        </div>
                      </div>
                      <p
                        className={`mt-2 text-xs ${
                          salesforceCompanyPrefixError ? 'text-red-600' : 'text-slate-500'
                        }`}
                      >
                        {salesforceCompanyPrefixError ||
                          `We'll connect using ${
                            salesforceLoginDomain ||
                            `examplecompany${SALESFORCE_MY_DOMAIN_SUFFIX}`
                          }.`}
                      </p>
                      <div className="mt-4 flex justify-end">
                        <Button
                          type="button"
                          onClick={() => void handleSalesforceCompanyDomainContinue()}
                          disabled={isLoading || !salesforceLoginDomain}
                          className="min-w-[220px] bg-blue-600 hover:bg-blue-700"
                        >
                          Continue with company domain
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">Connect Your CRM</h1>
          <p className="mt-2 text-sm text-gray-600">
            Choose which CRM system you'd like to connect to manage your customer data and
            applications.
          </p>
        </div>

        <div className="space-y-4">
          {providers.map((provider) => (
            <React.Fragment key={provider.id}>{renderProvider(provider)}</React.Fragment>
          ))}
        </div>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            You can change your CRM provider later in settings
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProviderSelectionModal;
